import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const label = (value) => (value || "unknown").replaceAll("_", " ");

export default function SharingTreeDetachedReview() {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setReport(null);
    setError("");
    api.get("/sharing-tree/detached-review").then(({ data }) => {
      if (active) setReport(data);
    }).catch((e) => {
      if (active) setError(formatApiError(e));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, revision]);
  const rows = (report?.enrollments || []).filter((r) =>
    (!year || String(r.year) === year) && (!onlyMissing || r.client_missing));
  const years = [...new Set((report?.enrollments || []).map((r) => r.year).filter(Boolean))].sort().reverse();
  return <section className="rounded-xl border p-4 my-4" style={{ borderColor: "var(--fb-border)" }}>
    <button type="button" className="font-semibold underline" aria-expanded={open} onClick={() => setOpen(!open)}>
      Historical detached-request review
    </button>
    {open && <div className="space-y-3 mt-3 text-sm">
      <p>Read-only, all program years, including removed and merged-source enrollments. No records are changed.
        Names, contact information, private request text, and audit snapshots are not shown.</p>
      <p role="note">A surviving member record is evidence only of what remains now. No member record here does not
        prove a request was deleted or ever existed. A merge destination is not proof of child identity or request preservation.
        Missing years and printed IDs are shown as unknown, never reconstructed from names.</p>
      <p>Any correction requires a separate staff review and explicit approval of the exact record IDs,
        program year, destination, and supporting evidence. This report cannot approve, relink, or restore records.</p>
      <button type="button" className="underline" disabled={loading} onClick={() => setRevision(revision + 1)}>Refresh report</button>
      {loading && <p role="status">Loading historical evidence…</p>}
      {error && <p role="alert">{error}</p>}
      {report && <>
        <p>{report.summary.enrollments} enrollments reviewed; {report.summary.missing_client} missing client links;
          {" "}{report.summary.merge_evidence} evidence entries. This is a current database read, not a backup comparison.</p>
        <div className="flex flex-wrap gap-4">
          <label>Program year <select value={year} onChange={(e) => setYear(e.target.value)} className="border rounded p-1">
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select></label>
          <label><input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} /> Only missing client links</label>
        </div>
        {!rows.length && <p>No enrollments match these filters.</p>}
        {rows.map((r) => <details key={r.household_record_id} className="border rounded p-3 break-words">
          <summary>{r.year || "Unknown year"} · {r.print_label || r.printed_id || "No printed household ID"} · {label(r.survival)}
            {r.removed ? " · removed/merged source" : ""}</summary>
          <p>Stored printed ID: {r.printed_id || "unknown"}. Household record: {r.household_record_id}; client record: {r.client_id || "missing/invalid"}.</p>
          <p>Client merge: {label(r.client_merge.status)} {r.client_merge.candidate_id || ""}</p>
          <p>Household merge: {label(r.household_merge.status)} {r.household_merge.candidate_id || ""}</p>
          <p>Family request text present: {r.family_request_present ? "yes (not displayed)" : "no"}.</p>
          {!r.members.length && <p>No surviving member record attached here. Review merge destinations below; do not assume deletion.</p>}
          <ul>{r.members.map((m) => <li key={m.record_id}>
            {m.print_label || m.printed_id || "No printed member ID"} (stored: {m.printed_id || "unknown"}) · record {m.record_id} · year {m.year || "unknown"} ·
            {" "}{m.structured_request_count} structured requests; private/legacy request text {m.private_request_present ? "present (not displayed)" : "absent"}
          </li>)}</ul>
        </details>)}
        <details><summary className="font-semibold">Recorded merge evidence (all years, not filtered)</summary>
          {!report.evidence.length && <p>No surviving merge evidence found.</p>}
          <ul className="space-y-3 mt-2">{report.evidence.map((e, i) => <li key={i} className="break-words">
            {e.kind} · {label(e.origin)} · {e.source_id || "unknown source"} → {e.target_id || "unknown destination"}.
            {" "}Source {e.source_present ? "present" : "not found"}; destination {e.target_present ? "present" : "not found"}.
            {" "}Years: {e.source_year || "unknown"} → {e.target_year || "unknown"}.
            {" "}Printed IDs: {e.source_printed_id || "unknown"} → {e.target_printed_id || "unknown"}.
            {" "}{label(e.resolution.status)} {e.resolution.candidate_id || ""}.
            {" "}Source record state: {label(e.source_survival)}.
            {!e.valid_ids && " Incomplete evidence: invalid or missing record ID."}
            {e.source_snapshot && <p>Retired member snapshot survives: {e.source_snapshot.structured_request_count} structured requests;
              private/legacy text {e.source_snapshot.private_request_present ? "present" : "absent"}. Snapshot evidence only, not a restored member.</p>}
          </li>)}</ul>
        </details>
        <details><summary className="font-semibold">Member records with no surviving household ({report.members_without_household.length})</summary>
          <ul>{report.members_without_household.map((m) => <li key={m.record_id} className="break-words">
            {m.year || "Unknown year"} · {m.print_label || m.printed_id || "No printed member ID"} · {m.record_id} ·
            {" "}{m.structured_request_count} structured requests; private/legacy text {m.private_request_present ? "present" : "absent"}.
            {" "}Missing household record: {m.household_record_id || "missing/invalid"}.
          </li>)}</ul>
        </details>
      </>}
    </div>}
  </section>;
}
